import type { ReactNode } from 'react';
import {
  type DatePickerDateType,
  DateTimeInput,
  EntitySnippet,
  type FileUploadDone,
  Form,
  FormSection,
  ImageDraggerWithCrop,
  RichTextInput,
  TextInput,
  type UploadChangeParam,
  type UploadedFileList,
  useGenerateImageUploadRulesAndValidations,
} from 'ui';
import { images, loadImage } from 'common/utils';
import { uploadImage } from 'features/files';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: fix according to result of #67489
import { OrganizationSelect } from 'features/organizations/components/organization-select';

export type OrganizationOption = {
  customChannels: boolean;
  label: ReactNode;
  tags: Array<{ id: string; name: string }>;
  value: string;
};

export type VideoBasicInfoFormValues = {
  coverPhoto?: [FileUploadDone];
  htmlDescription: string;
  name: string;
  organization: OrganizationOption;
  recordedAt: DatePickerDateType;
  textDescription: string;
};

type P = {
  mode: 'create' | 'edit';
};

const VideoBasicInfoFormContent = ({ mode }: P) => {
  const { rules: heroRules, validations: heroValidations } = useGenerateImageUploadRulesAndValidations();

  return (
    <FormSection title={{ children: 'Basic information' }}>
      <Form.Item<VideoBasicInfoFormValues>
        getValueFromEvent={({ fileList }: UploadChangeParam): UploadedFileList => fileList}
        label="Hero image"
        name="coverPhoto"
        rules={heroRules}
        tooltip="Members will see this image preview in Channels and My Content."
        valuePropName="fileList"
      >
        <ImageDraggerWithCrop
          dragger={{
            loadImage,
            shape: 'rectangle',
            size: 'large',
            uploadFile: uploadImage,
            validations: heroValidations,
          }}
        />
      </Form.Item>
      <TextInput
        item={{
          label: 'Title',
          name: 'name',
          rules: [{ required: true }],
        }}
      />
      <RichTextInput
        html={{
          label: 'Description',
          name: 'htmlDescription',
          rules: [{ required: true }],
        }}
        text={{ name: 'textDescription' }}
      />
      <Form.Item<VideoBasicInfoFormValues> label="Organization" name="organization" rules={[{ required: true }]}>
        <OrganizationSelect<OrganizationOption>
          createOption={(organization) => ({
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
            customChannels: organization.customChannels ?? false,
            label: (
              <EntitySnippet
                avatarProps={{
                  fallbackPhoto: images.default.organizationProfilePhoto,
                  photo:
                    organization.squareLogo?.url ??
                    organization.profilePhoto?.url ??
                    images.default.organizationProfilePhoto,
                  size: 'small',
                }}
                titleProps={{ children: organization.name }}
              />
            ),
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
            tags: organization.tags ?? [],
            value: organization.id,
          })}
          disabled={mode === 'edit'}
          query={{ filter: { scope: 'admin' } }}
        />
      </Form.Item>
      <DateTimeInput
        input={{
          maximum: new Date(),
          showTime: true,
        }}
        item={{
          label: 'Recorded at',
          name: 'recordedAt',
          rules: [{ required: true }],
        }}
      />
    </FormSection>
  );
};

export default VideoBasicInfoFormContent;
